import { apiClient } from '@/config/api';
import { ProjectMembership, HydraCollection } from '@/types';

export interface GetProjectMembershipsParams {
  page?: number;
  itemsPerPage?: number;
  project?: string; // IRI: /api/projects/{id}
  user?: string; // IRI: /api/users/{id}
}

export interface CreateProjectMembershipData {
  user: string; // IRI: /api/users/{id}
  project: string; // IRI: /api/projects/{id}
  role: 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER';
}

export interface UpdateProjectMembershipData {
  user?: string; // IRI: /api/users/{id}
  project?: string; // IRI: /api/projects/{id}
  role?: 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER';
}

export interface UpdateProjectMemberAdminData {
  fullName: string;
  phone?: string;
  isActive: boolean;
  role: 'PROJECT_ADMIN' | 'PROJECT_MANAGER' | 'PROJECT_USER';
  password?: string;
  confirmPassword?: string;
}

export const projectMembershipService = {
  // Get memberships collection (API Platform /project_memberships)
  async getMemberships(params?: GetProjectMembershipsParams): Promise<ProjectMembership[]> {
    const page = params?.page || 1;
    const itemsPerPage = params?.itemsPerPage || 50;

    const requestParams: Record<string, unknown> = {
      'groups[]': ['membership:read', 'user:read', 'project:read'],
      page,
      itemsPerPage,
    };

    if (params?.project) requestParams['project'] = params.project;
    if (params?.user) requestParams['user'] = params.user;

    const response = await apiClient.get<HydraCollection<ProjectMembership>>('/project_memberships', {
      params: requestParams,
    });

    const data = response.data;
    return data['hydra:member'] || data['member'] || [];
  },

  // Get all memberships for a project
  async getMembershipsByProject(projectId: number): Promise<ProjectMembership[]> {
    // Admin-only endpoint (separated from client/dashboard endpoints)
    const response = await apiClient.get<ProjectMembership[]>(`/admin/projects/${projectId}/memberships`);
    return response.data;
  },

  // Admin-only: update member (user fields + project role) in ONE endpoint
  async updateProjectMember(projectId: number, membershipId: number, data: UpdateProjectMemberAdminData): Promise<ProjectMembership> {
    const response = await apiClient.put<ProjectMembership>(`/admin/projects/${projectId}/memberships/${membershipId}`, data);
    return response.data;
  },

  // Get all memberships for a user
  async getMembershipsByUser(userId: number): Promise<ProjectMembership[]> {
    return this.getMemberships({ user: `/api/users/${userId}` });
  },

  // Get single membership
  async getMembership(id: number): Promise<ProjectMembership> {
    const response = await apiClient.get<ProjectMembership>(`/project_memberships/${id}`, {
      params: {
        'groups[]': ['membership:read', 'user:read'],
      },
    });
    return response.data;
  },

  // Create membership
  async createMembership(data: CreateProjectMembershipData): Promise<ProjectMembership> {
    const response = await apiClient.post('/project_memberships', {
      ...data,
      'groups[]': ['projectMembership:write'],
    });
    return response.data;
  },

  // Update membership
  async updateMembership(id: number, data: UpdateProjectMembershipData): Promise<ProjectMembership> {
    const response = await apiClient.put(`/project_memberships/${id}`, data);
    return response.data;
  },

  // Delete membership
  async deleteMembership(id: number): Promise<void> {
    await apiClient.delete(`/project_memberships/${id}`);
  },
};
